import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { GetManyResult } from 'core/types';
import { documentService } from 'pricing/services';
import { IPricingProfileDocument } from 'pricing/types';

export const useGetPricingProfileDocuments = (
  pricingProfileId: number,
  options: { enabled?: boolean } = {}
): GetManyResult<IPricingProfileDocument> => {
  const { data, isLoading, isError } = useQuery(
    ['pricingProfileDocuments', pricingProfileId],
    ({ signal }) =>
      documentService.getPricingProfileDocuments(
        {
          filters: { pricingProfileId },
        },
        signal
      ),
    options
  );

  return {
    records: data?.records ?? [],
    count: data?.count ?? 0,
    isLoading,
    isError,
  };
};

export const usePricingProfileDocumentActions = () => {
  return {
    ...useRemovePricingProfileDocument(),
    ...useSavePricingProfileDocument(),
  };
};

const useRemovePricingProfileDocument = () => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messagePricingProfileDocumentRemoveSuccess') },
    loading: { message: t('messagePricingProfileDocumentRemoveInProgress') },
    error: { title: t('errorTitlePricingProfileDocumentRemove') },
    position: NotificationPosition.TOP,
    invalidateQueries: ['pricingProfileDocuments'],
  });

  const { mutateAsync } = useMutation(documentService.removePricingProfileDocument, {
    ...errors,
  });

  return { removePricingProfileDocument: mutateAsync };
};

const useSavePricingProfileDocument = () => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    success: { message: t('messagePricingProfileDocumentSaveSuccess') },
    loading: { message: t('messagePricingProfileDocumentSaveInProgress') },
    error: { title: t('errorTitlePricingProfileDocumentSave') },
    position: NotificationPosition.TOP,
    invalidateQueries: ['pricingProfileDocuments'],
  });

  const { mutateAsync } = useMutation(documentService.savePricingProfileDocument, {
    ...errors,
  });

  return { savePricingProfileDocument: mutateAsync };
};
